import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import Axios from "../utils/http-request";

const COOKIE_KEYS = {
  tokenRate: "tokenRate",
  conversionRates: "conversionRates",
  userBalance: "userBalance",
};

export const useWalletSettings = (token: string | null) => {
  // --- SETTINGS QUERY ---
  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await Axios.get(`/admin/settings/setting`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data;
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // ⏳ 5 minutes freshness
    gcTime: 1000 * 60 * 10, // ♻️ cache lives 10 mins before GC
    refetchOnWindowFocus: false,
  });

  // ✅ Move side effects here
  useEffect(() => {
    if (settingsQuery.data) {
      Cookies.set(
        COOKIE_KEYS.tokenRate,
        settingsQuery.data?.rates?.token || "",
        { path: "/" }
      );
      Cookies.set(
        COOKIE_KEYS.conversionRates,
        JSON.stringify(settingsQuery.data?.walletsMeta?.rates || {}),
        { path: "/" }
      );
    }
  }, [settingsQuery.data]);

  // --- BALANCE QUERY ---
  const balanceQuery = useQuery({
    queryKey: ["walletBalance"],
    queryFn: async () => {
      const res = await Axios.get(`/wallets/user/wallets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data;
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // ⏳ 5 minutes freshness
    gcTime: 1000 * 60 * 10, // ♻️ cache duration before garbage collection
    refetchOnWindowFocus: false,
  });

  // ✅ Handle side effects after query success
  useEffect(() => {
    if (balanceQuery.data) {
      const tokenWallet = Array.isArray(balanceQuery.data)
        ? balanceQuery.data.find((wallet: any) => wallet.type === "token")
        : null;

      if (tokenWallet) {
        Cookies.set(COOKIE_KEYS.userBalance, tokenWallet.balance.toString(), {
          path: "/",
        });
      }
    }
  }, [balanceQuery.data]);

  const tokenRate =
    settingsQuery.data?.rates?.token ||
    Number(Cookies.get(COOKIE_KEYS.tokenRate) || 0);
  const conversionRates =
    settingsQuery.data?.walletsMeta?.rates ||
    (Cookies.get(COOKIE_KEYS.conversionRates)
      ? JSON.parse(Cookies.get(COOKIE_KEYS.conversionRates) as string)
      : {});

  const userBalance =
    (Array.isArray(balanceQuery.data)
      ? balanceQuery.data.find((wallet: any) => wallet.type === "token")
          ?.balance
      : null) || Number(Cookies.get(COOKIE_KEYS.userBalance) || 0);

  // ✅ Check if user can perform an action
  const canPerformAction = (requiredRate: number) =>
    userBalance >= requiredRate;

  return {
    tokenRate,
    conversionRates,
    userBalance,
    isLoading: settingsQuery.isLoading || balanceQuery.isLoading,
    refetchSettings: settingsQuery.refetch,
    refetchBalance: balanceQuery.refetch,
    canPerformAction,
  };
};
